// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePaged, Pager, DEFAULT_PAGE_SIZE, preferredPageSize, rememberPageSize } from '../../components/Pager'
import SkeletonRows from '../../components/SkeletonRows'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, AlertCircle, Download } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { matchesDob, formatCell } from '../../api/format'
import { useSmartFilters, type FilterDef } from '../../components/SmartFilters'
import { useAutoRefresh, VIEW_REFRESH_MS } from '../../components/useAutoRefresh'
import { RefreshControl } from '../../components/RefreshControl'
import { useStickyState } from '../../components/useStickyState'
import { WorkBenchCell, WorkBenchPill, useWorkBenchDefs } from '../../components/workflow/WorkBench'
import { RxRowDrawer, RxDrawerHint, type DrawerField } from '../../components/workflow/RxRowDrawer'
import BulkFieldEditor from '../../components/workflow/BulkFieldEditor'
import { useAuth } from '../../auth/AuthContext'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useColumnManager } from '../../components/ColumnManager'
import CategoryBadge from '../../components/common/CategoryBadge'
import { ColumnFilterButton, ColumnFilterChips, applyColumnFilters, filterToken, decomposeMulti, columnFilterSig, funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'
import { useColumnWidths, ColumnResizeHandle } from '../../components/common/ColumnResize'
import { listScrollStyle } from '../../styles/listScroll'
import { rowKey, rxIdOf, patchRows, findRow } from './customViewRows'

/** Cache: view data keyed by view id. Survives tab switches. */
type ColMeta = { label: string; source_table: string; column_name: string }
const viewCache = new Map<string, { columns: string[]; columns_meta: ColMeta[]; rows: Record<string, any>[]; runtime_ms: number; loadedAt: number }>()

/** Server's interactive ceiling for /views/{id}/data (exports get 250k — see ASP-77). */
const VIEW_ROW_LIMIT = 5000

export default function CustomViewTab() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const wbDefs = useWorkBenchDefs()
  // BULK-1: multi-select rows → bulk-change OwlRx-owned fields (custom + workbench).
  // Providers are read-only on these surfaces, so no selection UI for them.
  const { me } = useAuth()
  const canBulkEdit = me?.role_name !== 'provider' && me?.role_name !== 'provider_nurse'
  // BULK-1: selected ROW keys (fills at fill grain), and the prescription each belongs to —
  // remembered at selection time, because a ticked row may be on a page no longer loaded.
  const [bulkSel, setBulkSel] = useState<string[]>([])
  const bulkRx = useRef(new Map<string, string>())
  const [showBulk, setShowBulk] = useState(false)
  useEffect(() => { setBulkSel([]) }, [id])   // selection never crosses views
  const [data, setData] = useState<{ columns: string[]; columns_meta: ColMeta[]; rows: Record<string, any>[]; runtime_ms: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // D-PERF-1 P2: server-side pagination is the DEFAULT for custom views. The /views/{id}/data endpoint
  // already pages (OFFSET/FETCH) AND returns an exact total (ASP-53) and applies the view's configured
  // sort server-side — so the default view loads one page (with a true page X of Y) instead of the 5,000-
  // row ceiling. The moment a user layers on an ad-hoc search / filter / header sort, we fall back to the
  // full-fetch + client pipeline (the round-table red line: never server-page while filter/sort is
  // client-side). Mode is computed after the filter/sort inputs exist and read by loadView via a ref.
  const [srvPage, setSrvPage] = useState(1)
  const [srvPageSize, setSrvPageSize] = useState(() => preferredPageSize())
  const [srvTotal, setSrvTotal] = useState(0)
  const serverModeRef = useRef(false)
  const srvPageRef = useRef(1)
  const srvPageSizeRef = useRef(preferredPageSize())
  // D-VIEWS-SRV-1 WS1: loadView is a stable useCallback that reads its inputs from refs (so a
  // silent auto-refresh tick never re-subscribes). The header sort has to arrive the same way,
  // or a tick would send whatever sort was current when the callback was created.
  const srvSortRef = useRef<{ col: string | null; dir: 'asc' | 'desc' | null }>({ col: null, dir: null })
  // D-VIEWS-SRV-1 WS3: the funnel selections travel the same way — read from a ref by loadView.
  const srvFiltersRef = useRef<api.ViewAdHocFilter[]>([])

  // BADGE-SAFE (P0 2026-08-06 postmortem): single-flight. When the DB slows past the 15s
  // auto-refresh interval, a new tick used to launch a SECOND execution on top of the
  // unfinished one — the pile-up that reached the tenant DB's worker cap (Azure SQL 10928).
  // A silent tick that finds a load in flight is simply skipped; the next tick catches up.
  const loadInFlight = useRef(false)
  const loadView = useCallback(async (force = false, silent = false) => {
    if (!id) return
    if (silent && loadInFlight.current) return
    loadInFlight.current = true
    try {
    // D-PERF-1 P2: server-paged default view — fetch ONE page (the endpoint applies the view's sort and
    // returns an exact total). Bypasses the full-set cache (each page is a cheap single fetch).
    if (serverModeRef.current) {
      if (!silent) setLoading(true)
      setError(null)
      try {
        const s = srvSortRef.current
        const d = await api.executeView(id, {
          limit: srvPageSizeRef.current,
          offset: (srvPageRef.current - 1) * srvPageSizeRef.current,
          // WS3: funnel selections are part of the QUERY; the returned total is the filtered total.
          filters: srvFiltersRef.current,
          // No header click → omit both, and the endpoint applies the view's saved sort exactly
          // as it always has.
          ...(s.col && s.dir ? { sort: s.col, dir: s.dir } : {}),
        })
        setData({ columns: d.columns, columns_meta: d.columns_meta ?? [], rows: d.rows, runtime_ms: d.runtime_ms })
        setSrvTotal(typeof d.total === 'number' ? d.total : d.rows.length)
      } catch (e: any) { setError(friendlyError(e)) }
      finally { setLoading(false) }
      return
    }
    // Use cache if available, FRESH, and not forced refresh. RX-PERF-8: `loadedAt` was stored
    // but never read — a cached view served stale worklist rows for the whole session unless
    // the user clicked refresh. Queues are live; 60s is long enough to make tab-flipping free
    // and short enough that a returning user sees current data.
    const cached = !force ? viewCache.get(id) : undefined
    if (cached && Date.now() - cached.loadedAt < 60_000) {
      setData(cached); setLoading(false); return
    }
    if (!silent) setLoading(true)
    setError(null)
    try {
      // ASP-77 follow-up (Patrick 2026-07-24: "accurate data in all reports"): the on-screen
      // report used to request 2,000 rows and say nothing when a view had more — the same
      // silent-partial pattern as the Rx list. Ask for the server's interactive ceiling and
      // disclose when we bind; the CSV export covers the full view (250k ceiling).
      const d = await api.executeView(id, { limit: VIEW_ROW_LIMIT })
      const entry = { columns: d.columns, columns_meta: d.columns_meta ?? [], rows: d.rows, runtime_ms: d.runtime_ms, loadedAt: Date.now() }
      viewCache.set(id, entry)
      setData(entry)
    } catch (e: any) { setError(friendlyError(e)) }
    finally { setLoading(false) }
    } finally { loadInFlight.current = false }
  }, [id])

  useEffect(() => { loadView() }, [loadView])

  // D-VIEWS: every 15s, visibility-gated, silent (rows swap in place; filters/sort/scroll untouched)
  // Review fix (2026-08-14): the auto-refresh interval was sized for 10-row pages; at the
  // new 100-row default the same tick reloads 10x the rows per open view against 0.5–1 vCore
  // tenant DBs. Large pages refresh at 4x the base interval; focus/manual refresh unchanged.
  const ar = useAutoRefresh(() => loadView(true, true),
    { intervalMs: srvPageSize > 25 ? VIEW_REFRESH_MS * 4 : VIEW_REFRESH_MS })

  // Keep an inline edit consistent in the cached view data (so re-sorts and tab-switches
  // reflect it without a server refetch). A WorkBench value patches its own FILL row ('row');
  // a custom field patches every row of the prescription ('rx') — see customViewRows.ts.
  const applyCellEdit = useCallback((key: string, scope: 'row' | 'rx', label: string, value: string | null) => {
    setData(prev => {
      if (!prev) return prev
      const rows = patchRows(prev.rows, key, scope, label, value)
      const next = { ...prev, rows }
      if (id) {
        const cached = viewCache.get(id)
        if (cached) viewCache.set(id, { ...cached, rows })
      }
      return next
    })
  }, [id])

  // D-VIEWS-SRV-1 WS2: what each column can DO comes from the SERVER now.
  //
  // It used to be inferred from whatever rows happened to be loaded: a numeric column was never
  // filterable, a string over 50 chars was not, anything shaped like a date was not, and the
  // survivors were then capped at six funnels. So filterability depended on what the first 50
  // rows contained — which is why it read as arbitrary to pharmacists. It was arbitrary.
  //
  // Cached hard: a view's shape changes when someone edits the view, not per request.
  const capsQ = useQuery({
    queryKey: ['views', id, 'columns'],
    queryFn: () => api.getViewColumnCaps(id!),
    enabled: !!id,
    staleTime: 600_000,
  })
  const caps = useMemo(
    () => new Map((capsQ.data?.items ?? []).map(c => [c.key, c])),
    [capsQ.data])
  // Until the caps land, keep today's permissive behaviour: every header sortable, no funnel
  // claimed either way. Controls that appear and then disappear are worse than controls that
  // arrive a beat late.
  // D-UI-2: a column the server filters by CONTAINS and never enumerates (sig, notes).
  const isContainsOnly = (c: api.ViewColumnCap | undefined) =>
    !!c?.filterable && !c.facetable && (c.ops ?? []).includes('contains')
  const capOf = useCallback(
    (col: string) => {
      const c = caps.get(col)
      if (!c) return { sortable: true, filterable: false }
      return { sortable: c.sortable, filterable: c.filterable }
    },
    [caps])

  // Build smart filter defs from the columns the server says are filterable.
  const filterDefs: FilterDef[] = useMemo(() => {
    if (!data?.columns) return []
    // No caps yet (first paint, or the endpoint failed): offer nothing rather than guess. A
    // funnel that appears and then vanishes is worse than one that arrives a beat late.
    if (!caps.size) return []
    return data.columns
      // The dropdown row lists values; a contains-only column (free text) has none to list.
      .filter(col => caps.get(col)?.filterable === true && !isContainsOnly(caps.get(col)))
      .map(col => ({
        key: col,
        label: col,
        getValue: (row: any) => row[col] != null ? String(row[col]) : null,
      }))
  }, [data, caps])

  const searchFields = useMemo(() => {
    if (!data?.columns) return []
    return data.columns.map(col => (row: any) => row[col] != null ? String(row[col]) : '')
  }, [data])

  // ASP-56: DOB typed in ANY of the six supported formats matches date-ish columns
  // (dob / date_of_birth / birth) even though the cell renders differently.
  const searchPredicates = useMemo(() => {
    if (!data?.columns) return []
    const dobCols = data.columns.filter(c => /dob|birth/i.test(c))
    if (dobCols.length === 0) return []
    return [(q: string, row: any) => dobCols.some(c => matchesDob(q, row[c] != null ? String(row[c]) : null))]
  }, [data])

  // WS3 follow-up (ASP 2026-09-28, Patrick: "the dropdown filters in VA Welcome Call Queue are
  // empty"): the dropdown row above the grid is a loaded-rows device — each dropdown lists the
  // values on the 25 rows in memory, and picking one dropped the tab out of server mode onto a
  // bounded client fetch. On an 837-row queue 7 of 32 dropdowns were empty and the rest were a
  // sample presented as the whole. In server mode the header funnels are the filter surface
  // (server-fed, multi-select, whole-view values), so the dropdown row is not drawn there; it
  // returns with the free-text search, which is the one path that is still client-side (WS4).
  // The search string is owned here (same sticky key the hook used) so the decision can be made
  // before the hook runs.
  const [smartSearchState, setSmartSearchState] = useStickyState<string>(id ? `cv:${id}:smart:search` : null, '')
  const dropdownDefs = smartSearchState.trim() ? filterDefs : []
  const { filtered, filterBar, search: smartSearch, filterSig } = useSmartFilters(
    data?.rows || [], dropdownDefs, { searchFields, searchPredicates, persistKey: id ? `cv:${id}:smart` : undefined,
      searchState: [smartSearchState, setSmartSearchState] }
  )

  const { visibleColumns, managerUI, toggleButton } = useColumnManager(
    id || 'unknown', data?.columns || []
  )

  // D-VIEWS-COLUMN-RESIZE (86cb27wub): per-view column widths, durable across sessions. Custom
  // views key on the view id, so two views showing the same column label keep separate layouts.
  const colResize = useColumnWidths(id ? `cv:${id}` : null, visibleColumns)

  // Map a column's display label → its WorkBench field-def (if it's a rx_workbench
  // column), so those cells render as editable dropdowns instead of read-only text.
  const wbByLabel = useMemo(() => {
    const m: Record<string, { field_key: string }> = {}
    for (const cm of (data?.columns_meta ?? []))
      // E-CV-202: updated_at/updated_by are STAMP columns, not editable field-defs —
      // routing them to WorkBenchCell rendered raw ISO (Gate 16). They fall through
      // to formatCell (local MM-DD-YYYY, h:mm AM/PM) like any other timestamp.
      if (cm.source_table === 'rx_workbench' && cm.column_name !== 'updated_at' && cm.column_name !== 'updated_by')
        m[cm.label] = { field_key: cm.column_name }
    return m
  }, [data?.columns_meta])

  // CV-1a/b/c: custom-field columns (rx_custom_field_values) and each column's data_type.
  // This is what lets a custom view render a dropdown / date picker / text by the field's
  // DECLARED type (from the server, never inferred from the name — Priya's condition),
  // matching the All view instead of falling through to plain "—".
  //
  // CV-6: the executor names these columns with a SYNTHETIC id — `cfd_{mappingId}_{defGuid}`
  // (ViewBuilderFunctions virtual catalog). The def GUID must be EXTRACTED: using the raw
  // synthetic name meant cfDefs[...] never matched (dropdown fields rendered with no options)
  // and the PUT sent a non-GUID field_id the server rejected — edits silently reverted.
  const cfByLabel = useMemo(() => {
    const m: Record<string, { field_def_id: string; data_type: string }> = {}
    for (const cm of (data?.columns_meta ?? []))
      if (cm.source_table === 'rx_custom_field_values') {
        const defId = /^cfd_\d+_(.+)$/.exec(cm.column_name)?.[1] ?? cm.column_name
        m[cm.label] = { field_def_id: defId, data_type: (cm as any).data_type ?? 'string' }
      }
    return m
  }, [data?.columns_meta])
  // CV-2c: defs by id → dropdown options for inline choice editing.
  // BADGE-SAFE (crawl finding): this was keyed on the cfByLabel OBJECT, whose identity
  // changes with every 15s grid refresh (columns_meta is a fresh array each response), so
  // /custom-fields refetched 4-5×/min per user for config that changes ~never. Keyed on the
  // stable def-id SIGNATURE now: one fetch per actual column-set change.
  const cfDefSig = useMemo(
    () => Object.values(cfByLabel).map(x => x.field_def_id).sort().join(','), [cfByLabel])
  const [cfDefs, setCfDefs] = useState<Record<string, api.CustomFieldDef>>({})
  useEffect(() => {
    if (cfDefSig === '') return
    let dead = false
    api.listCustomFieldDefs().then(r => {
      if (!dead) setCfDefs(Object.fromEntries((r.items ?? []).map(d => [d.id, d])))
    }).catch(() => {})
    return () => { dead = true }
  }, [cfDefSig])

  // D-VIEWS-SRV-1 WS1: the grid keys everything by DISPLAY LABEL (rows are aliased `AS [label]`
  // server-side), but the sort whitelist on the server matches the PHYSICAL column name. Send a
  // label and it matches nothing, the sort is silently dropped, and the grid renders the saved
  // order under a sort arrow — worse than not sorting, because it looks sorted. columns_meta
  // already carries the pair, so resolve here.
  //
  // FIRST WINS on a duplicate label, matching the redactor's own rule for the same ambiguity
  // (ViewBuilderFunctions labelToCol: "a label collision within one table would make the fold
  // ambiguous, so keep the FIRST"). Labels are user-editable and can collide within a view.
  const colNameByLabel = useMemo(() => {
    const m: Record<string, string> = {}
    for (const cm of (data?.columns_meta ?? [])) {
      const cn = (cm as any).column_name
      if (cn && !(cm.label in m)) m[cm.label] = cn
    }
    return m
  }, [data?.columns_meta])

  const dtByLabel = useMemo(() => {
    const m: Record<string, string> = {}
    for (const cm of (data?.columns_meta ?? [])) m[cm.label] = (cm as any).data_type ?? 'string'
    return m
  }, [data?.columns_meta])
  // Numeric columns get a Min/Max funnel (bounds pushed down as gte/lte), never a value list.
  const numericColSet = useMemo(() => {
    const s = new Set<string>()
    for (const [label, dt] of Object.entries(dtByLabel))
      if (['number', 'numeric', 'int', 'integer', 'decimal', 'money', 'float'].includes(dt)) s.add(label)
    return s
  }, [dtByLabel])

  // CV-7: date-typed columns — declared type first (columns_meta), value shape as fallback
  // (older cached frames / columns whose meta lost the type). Drives the funnel filter's
  // DATE-RANGE mode: without it, "Workbench Last Update" offered a checkbox list of raw ISO
  // timestamps (every row a unique 2026-08-06T01:57:30.99… option — unreadable and useless).
  const dateColSet = useMemo(() => {
    const s = new Set<string>()
    for (const cm of (data?.columns_meta ?? []))
      if (['date', 'datetime'].includes((cm as any).data_type ?? '')) s.add(cm.label)
    for (const c of (data?.columns ?? [])) {
      if (s.has(c)) continue
      for (let i = 0; i < Math.min(data?.rows.length ?? 0, 50); i++) {
        const v = data!.rows[i][c]
        if (v == null) continue
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v)) s.add(c)
        break   // first non-null value decides, same sampling rule as filterDefs
      }
    }
    return s
  }, [data])

  // RX-DRAWER-1: ?rxd=<row key> opens the right-click row drawer, same contract as the
  // standard tabs — keyed by id (survives the 15s grid refresh + re-sort), rendered from
  // the already-loaded row, zero extra API calls, URL-restorable. The key is the row's FILL at
  // fill grain (ticket #1022): keyed by prescription, it opened whichever sibling came first.
  const drawerId = searchParams.get('rxd')
  const lastDrawerRow = useRef<Record<string, any> | null>(null)
  const drawerRow = useMemo(() => {
    if (!drawerId) { lastDrawerRow.current = null; return null }
    const found = findRow(data?.rows ?? [], drawerId)
    if (found) lastDrawerRow.current = found
    return found ?? (lastDrawerRow.current && findRow([lastDrawerRow.current], drawerId) ? lastDrawerRow.current : null)
  }, [drawerId, data])
  const openDrawer = (rxId: string) => {
    const next = new URLSearchParams(searchParams); next.set('rxd', rxId); setSearchParams(next)
  }
  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams); next.delete('rxd'); setSearchParams(next, { replace: true })
  }

  // D-ASP-VB-1 item 4: DrugReference category columns render as the colored CategoryBadge
  // (per-pharmacy label colors), not plain text — matching every other Rx surface.
  const drugCatLabels = useMemo(() => new Set(
    (data?.columns_meta ?? [])
      .filter(cm => cm.source_table === 'drug_reference' && cm.column_name === 'category')
      .map(cm => cm.label)
  ), [data?.columns_meta])
  const [catColors, setCatColors] = useState<Record<string, string | null>>({})
  // BADGE-SAFE (crawl finding): keyed on the SET identity before — a fresh Set per 15s grid
  // refresh meant /drug-reference/categories refetched ~5×/min per user. Stable signature now.
  const drugCatSig = useMemo(() => Array.from(drugCatLabels).sort().join(','), [drugCatLabels])
  useEffect(() => {
    if (drugCatSig === '') return
    let dead = false
    api.listDrugCategories()
      .then(r => { if (!dead) setCatColors(Object.fromEntries(r.items.map(c => [c.name, c.color ?? null]))) })
      .catch(() => {})  // module gate / transient: CategoryBadge falls back to the default palette
    return () => { dead = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drugCatSig])

  // Column sorting
  const [sortCol, setSortCol] = useStickyState<string | null>(id ? `cv:${id}:sortCol` : null, null)
  const [sortDir, setSortDir] = useStickyState<'asc' | 'desc' | null>(id ? `cv:${id}:sortDir` : null, null)
  const clickHeader = (col: string) => {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return }
    if (sortDir === 'asc') { setSortDir('desc'); return }
    setSortCol(null); setSortDir(null)
  }
  // G-UI-1 / ASP-68 header filters — rows are plain records keyed by column label.
  const [colFilters, setColFilters] = useStickyState<ColumnFilters>(id ? `cv:${id}:colFilters` : null, {})
  const cvValueOf = (row: Record<string, any>, key: string) => row[key]
  // 86cb27pa9 Part 2: multi-select columns, by LABEL (custom-view columns are keyed by their
  // display label). Source is the WorkBench field def's selection_mode — never inferred from the
  // presence of a comma, which is a legitimate character inside a single value.
  const multiValueKeys = useMemo(() => {
    const s = new Set<string>()
    for (const [label, def] of Object.entries(wbByLabel)) {
      if (wbDefs[def.field_key]?.selection_mode === 'multi') s.add(label)
    }
    return s
  }, [wbByLabel, wbDefs])
  // D-VIEWS-SRV-1 WS3: translate the header funnels into server filters. A column whose label
  // does not resolve to a physical name (meta not loaded yet) cannot be pushed; until it can,
  // the grid stays on the client pipeline for that selection rather than silently ignoring it.
  const { srvFilters, allPushable } = useMemo(() => {
    const out: api.ViewAdHocFilter[] = []
    let ok = true
    for (const [label, set] of Object.entries(colFilters)) {
      if (!set || set.size === 0) continue
      const column = colNameByLabel[label]
      if (!column) { ok = false; continue }
      // One translation for every server-paged list (D-UI-2). A free-text column answers
      // "searched, not enumerated", the funnel draws a contains box, and its term must go out
      // as `contains`, never as a value to tick.
      for (const p of funnelToServerOps(set, undefined).preds) out.push({ column, ...p })
    }
    return { srvFilters: out, allPushable: ok }
  }, [colFilters, colNameByLabel])
  // D-PERF-1 P2 / WS1 / WS3: server-page whenever everything the user asked for is expressible
  // in the query — the view's own sort or a header sort, and every funnel selection. Only the
  // free-text search (WS4, pending its measurement) still drops to the bounded client pipeline.
  const serverMode = !smartSearch.trim() && allPushable
  serverModeRef.current = serverMode
  srvFiltersRef.current = srvFilters
  // In server mode the rows ARE the answer: filtered by the database, with a true total. Re-applying
  // the funnels in the browser would only disagree with it (multi-value and blank semantics differ).
  const headerFiltered = useMemo(
    () => serverMode ? filtered : applyColumnFilters(filtered, colFilters, cvValueOf, multiValueKeys),
    [serverMode, filtered, colFilters, multiValueKeys])
  const distinctFor = (key: string): string[] => {
    const others: ColumnFilters = Object.fromEntries(Object.entries(colFilters).filter(([k]) => k !== key))
    const base = applyColumnFilters(filtered, others, cvValueOf, multiValueKeys)
    const seen = new Set<string>()
    const multi = multiValueKeys.has(key)
    for (const r of base) {
      const v = cvValueOf(r, key)
      if (multi || String(filterToken(v)).startsWith('[')) for (const t of decomposeMulti(v, multi)) seen.add(t)
      else seen.add(filterToken(v))
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }

  const sorted = useMemo(() => {
    if (!sortCol || !sortDir) return headerFiltered
    return [...headerFiltered].sort((a, b) => {
      const av = a[sortCol]; const bv = b[sortCol]
      if (av == null && bv == null) return 0
      if (av == null) return 1; if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av
      const as = String(av).toLowerCase(); const bs = String(bv).toLowerCase()
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as)
    })
  }, [headerFiltered, sortCol, sortDir])

  // Render one page at a time (selectable size) so large views don't dump every row into
  // the DOM. Sort/filter above still run over the full set.
  const clientPaged = usePaged(sorted, DEFAULT_PAGE_SIZE, `${id}|${filterSig}|${columnFilterSig(colFilters)}`)

  // D-PERF-1 P2: server-page the DEFAULT view — no ad-hoc search, no smart-filter, no column filter, and
  // the view's own configured sort (no header-click override, sortDir null). Any of those → client
  // pipeline over the full-fetch set (the red line). Server mode gets an EXACT total (page X of Y).
  // D-VIEWS-SRV-1 WS1: a header-click sort NO LONGER drops to the client pipeline. The sort
  // pushes into the query, so the grid stays one bounded page with an exact total. Search and
  // the column funnels still fall back (WS3/WS4) — each one removed from this condition is one
  // fewer path that reasons over 5,000 rows and calls the sample a total.
  // An EMPTY Set is not an active filter — applyColumnFilters ignores them
  // (`.filter(([, set]) => set.size > 0)`), but counting keys treated them as active. Clearing a
  // filter through its chip leaves the key with an empty Set, so the grid stayed in client mode
  // for the rest of the session. That used to cost a 5,000-row fetch; now it would also silently
  // send the sort back to the client pipeline, which is exactly what this change exists to stop.
  // (serverMode is decided above, before the client pipeline — WS3.)
  // Only a server-mode sort travels to the server, and only once the label resolves to a real
  // column name. An unresolved label (meta not loaded yet) sends nothing rather than a value the
  // server would reject — the saved sort then stands, which is the honest fallback.
  const srvSortCol = serverMode && sortCol ? (colNameByLabel[sortCol] ?? null) : null
  srvSortRef.current.col = srvSortCol
  srvSortRef.current.dir = srvSortCol ? sortDir : null
  srvPageRef.current = srvPage
  srvPageSizeRef.current = srvPageSize
  // Reset to page 1 when re-entering server mode, changing page size, or switching view.
  const prevServerMode = useRef(serverMode)
  useEffect(() => {
    const wasServer = prevServerMode.current
    prevServerMode.current = serverMode
    if (serverMode && !wasServer) setSrvPage(1)
    // A mode transition changes the data source (paged endpoint <-> full-fetch) — reload.
    if (serverMode !== wasServer) loadView(true)
  }, [serverMode, loadView])
  useEffect(() => { setSrvPage(1) }, [id, srvPageSize])
  // D-VIEWS-SRV-1 WS1: in server mode the sort is part of the QUERY, so changing it changes which
  // rows page 1 holds — it has to go back to page 1 and refetch. (In client mode the comparator
  // re-runs over rows already in memory and no fetch is wanted.) Skips the initial mount so the
  // first load is not doubled, the same guard the page-change effect below uses.
  // Keyed by view id, not a bare boolean: loadView is recreated when `id` changes, so this
  // effect re-runs on every view switch. A first-mount-only guard would let it fire there too —
  // a second concurrent fetch on top of the mount load, each carrying its own COUNT, against a
  // 0.5-1 vCore tenant DB with a ~75-worker cap. That is the 2026-08-06 10928 shape.
  const sortMountedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (sortMountedFor.current !== id) { sortMountedFor.current = id; return }
    if (!serverModeRef.current) return
    // Sorting from a later page must land on page 1 of the NEW order. Set the ref imperatively
    // (setSrvPage is async and the paged effect below reads the ref), then let that effect issue
    // the fetch, so sorting from page 3 does not fire two identical requests.
    if (srvPageRef.current !== 1) { srvPageRef.current = 1; setSrvPage(1); return }
    loadView(true)
  }, [id, sortCol, sortDir, loadView])
  // WS3: a funnel change changes the result set, so page 1 of the new set is what to show.
  const srvFilterSig = api.encodeViewFilters(srvFilters).join('|')
  const filterMountedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (filterMountedFor.current !== id) { filterMountedFor.current = id; return }
    if (!serverModeRef.current) return
    if (srvPageRef.current !== 1) { srvPageRef.current = 1; setSrvPage(1); return }
    loadView(true)
  }, [id, srvFilterSig, loadView])
  // Re-fetch one page when the server page/size changes. Skip the initial mount (the loadView-on-mount
  // effect already did the first load) so we don't double-fetch.
  const pagedMounted = useRef(false)
  useEffect(() => {
    if (!pagedMounted.current) { pagedMounted.current = true; return }
    if (serverModeRef.current) loadView(true)
  }, [srvPage, srvPageSize, loadView])

  // In server mode the fetched rows ARE the page → drive the table + <Pager> from server state with the
  // endpoint's exact total; otherwise the client pager. One set of names downstream.
  const page = serverMode ? srvPage : clientPaged.page
  const setPage = serverMode ? setSrvPage : clientPaged.setPage
  const pageSize = serverMode ? srvPageSize : clientPaged.pageSize
  const setPageSize = serverMode ? (n: number) => { rememberPageSize(n); setSrvPageSize(n) } : clientPaged.setPageSize
  const pageRows = serverMode ? (data?.rows ?? []) : clientPaged.pageRows
  const total = serverMode ? srvTotal : clientPaged.total
  const totalPages = serverMode ? Math.max(1, Math.ceil(srvTotal / srvPageSize)) : clientPaged.totalPages

  // RX-PERF-4 (skeleton loading): the full-page spinner is for the FIRST paint only (no data
  // yet). Every later load — page change, size change, mode flip, refresh — keeps the toolbar,
  // filters and pager mounted and shows skeleton rows in the tbody instead. Previously every
  // Next click unmounted the entire view for a centered spinner.
  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 size={20} className="spin" /> Loading view…</div>
  if (error) return <div style={errStyle}><AlertCircle size={14} /> {error}</div>
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-soft)' }}>No rows match this view.</div>
  if (!loading && data.rows.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-soft)' }}>No rows match this view.</div>

  return (
    <div style={{ padding: '0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
          {data.runtime_ms}ms · cached {Math.round((Date.now() - (viewCache.get(id!)?.loadedAt || Date.now())) / 1000)}s ago
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {toggleButton}
          <a href={`/api/views/${id}/data?format=csv`}   /* ASP-77: no client limit — server exports the full view */
             download
             style={{ ...refreshBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
             title="Download as CSV">
            <Download size={12} /> CSV
          </a>
          <RefreshControl ar={ar} />
        </div>
      </div>

      {managerUI}
      {filterBar}
      {serverMode && (
        <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', margin: '-6px 0 8px' }}>
          Filter with the funnel in each column header — the values come from the whole view, not this page.
        </div>
      )}

      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize}
             onPage={setPage} onPageSize={setPageSize} label="rows" />

      <div style={{ ...listScrollStyle(),
  overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
        {data.rows.length >= VIEW_ROW_LIMIT && (
        <div style={{ fontSize: 12.5, padding: '7px 10px', marginBottom: 8, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f' }}>
          Showing the first <b>{VIEW_ROW_LIMIT.toLocaleString()}</b> rows of this report. Counts and
          totals elsewhere are computed over the whole set — <b>use Export CSV for every row</b>.
        </div>
      )}
      <ColumnFilterChips filters={colFilters} labels={Object.fromEntries((data?.columns ?? []).map(c => [c, c]))} onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))} onClearAll={() => setColFilters({})} />
        {/* BULK-1: selection bar — appears the moment anything is ticked. */}
        {canBulkEdit && bulkSel.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', margin: '6px 0 8px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface-raised, var(--color-surface))' }}>
            <span style={{ fontSize: 13 }}>{bulkSel.length} selected</span>
            <button type="button" onClick={() => setShowBulk(true)}
              style={{ marginLeft: 'auto', border: 'none', background: 'var(--color-accent, #1a56db)', color: '#fff', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Bulk change fields ({bulkSel.length})
            </button>
            <button type="button" onClick={() => setBulkSel([])}
              style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>
              Clear
            </button>
          </div>
        )}
        <RxDrawerHint />
        <table ref={colResize.tableRef} className={colResize.tableClass}
          style={{
            width: colResize.totalWidth ? colResize.totalWidth + (canBulkEdit ? 34 : 0) : '100%',
            minWidth: colResize.totalWidth ? 0 : undefined,
            borderCollapse: 'collapse', fontSize: 12, tableLayout: colResize.tableLayout,
          }}>
          {/* D-VIEWS-COLUMN-RESIZE: same engine as StationQueue, keyed per custom view. */}
          <colgroup>
            {canBulkEdit && <col style={{ width: 34 }} />}
            {visibleColumns.map(c => <col key={c} style={{ width: colResize.widthFor(c) }} />)}
          </colgroup>
          <thead>
            <tr>
            {canBulkEdit && (
              <th style={{ ...thStyle, width: 34 }} aria-label="Select rows">
                <input type="checkbox" aria-label="Select all rows on this page"
                  checked={pageRows.length > 0 && pageRows.every(r => { const k = rowKey(r); return k == null || bulkSel.includes(k) })}
                  onChange={e => {
                    const ids = pageRows.map(r => { const k = rowKey(r); if (k) bulkRx.current.set(k, rxIdOf(r)!); return k })
                      .filter((k): k is string => k != null)
                    setBulkSel(prev => e.target.checked
                      ? Array.from(new Set([...prev, ...ids]))
                      : prev.filter(x => !ids.includes(x)))
                  }} />
              </th>
            )}
            {visibleColumns.map((c, ci) => (
              <th key={c}
                  title={caps.get(c)?.reason ?? undefined}
                  style={{ ...thStyle, cursor: capOf(c).sortable ? 'pointer' : 'default', userSelect: 'none' }}
                  onClick={() => { if (capOf(c).sortable) clickHeader(c) }}>
                {c}{sortCol === c ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}{' '}
                {/* WS2 / rule R1: a control that is off says WHY on hover, rather than silently
                    not being there. The old grid simply omitted the funnel and left the user to
                    wonder why this column behaved differently from the one beside it. */}
                {capOf(c).filterable && (
                  <ColumnFilterButton label={c}
                    getValues={() => distinctFor(c)}
                    // WS3: in server mode the option list is the column's values across the WHOLE
                    // view (its rules + the other funnels), not the 25 rows on this page — which is
                    // why a sparse column used to offer only "(blank)" until a sort moved values
                    // onto page 1. Client mode (free-text search active) keeps the loaded-rows list.
                    isText={isContainsOnly(caps.get(c))}
                    fetchOptions={serverMode && colNameByLabel[c] && !dateColSet.has(c) && !numericColSet.has(c) && !isContainsOnly(caps.get(c))
                      ? (term: string) => api.getViewFacet(id!, {
                          column: colNameByLabel[c], q: term,
                          filters: srvFilters.filter(f => f.column !== colNameByLabel[c]),
                        // Options read the way the cells do (origin codes as words, phones
                        // formatted); the VALUE stays the stored one so the filter binds exactly.
                        }).then(r => ({ ...r, options: r.options.map(o => ({ ...o, label: formatCell(c, o.value) })) }))
                      : undefined}
                    isDate={dateColSet.has(c)}
                    isNumeric={numericColSet.has(c)}
                    selected={colFilters[c] ?? new Set()}
                    onChange={next => { setColFilters(f => ({ ...f, [c]: next })); setPage(1) }} />
                )}
                <ColumnResizeHandle colKey={c} colIndex={ci} api={colResize} label={c} />
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows rows={Math.min(pageSize, 10)} cols={visibleColumns.length + (canBulkEdit ? 1 : 0)} />
            ) : pageRows.map((row, i) => {
              // rxId: the prescription (custom fields). key: this row's own record — the FILL at
              // fill grain — which is what opens, selects and takes a WorkBench edit (#1022).
              const rxId = rxIdOf(row)
              const key = rowKey(row)
              return (
                <tr
                  key={i}
                  onDoubleClick={() => key && navigate(`/rx/${key}`)}
                  onContextMenu={(e) => { if (key) { e.preventDefault(); openDrawer(key) } }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && key) navigate(`/rx/${key}`) }}
                  tabIndex={rxId ? 0 : undefined}
                  role={rxId ? 'button' : undefined}
                  title={rxId ? 'Double-click to open · right-click for quick actions' : undefined}
                  aria-label={rxId ? 'Open prescription (double-click to open)' : undefined}
                  // Zebra striping comes from the one global rule (index.css) — no inline copy.
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'default' }}
                >
                  {canBulkEdit && (
                    <td style={{ ...tdStyle, width: 34 }} onClick={e => e.stopPropagation()}>
                      {key && rxId && (
                        <input type="checkbox" aria-label="Select row for bulk change"
                          checked={bulkSel.includes(key)}
                          onChange={() => {
                            bulkRx.current.set(key, rxId)
                            setBulkSel(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key])
                          }} />
                      )}
                    </td>
                  )}
                  {visibleColumns.map(c => {
                    const wb = wbByLabel[c]
                    const cf = cfByLabel[c]
                    const dt = dtByLabel[c] ?? 'string'
                    return (
                      <td key={c} style={tdStyle}>
                        {wb && key
                          ? <WorkBenchCell
                              fieldDef={wbDefs[wb.field_key]}
                              // A WorkBench edit belongs to the FILL on screen (Rx 149012), and
                              // so does the optimistic patch: patching by prescription painted
                              // every sibling fill with this fill's answer (#1022).
                              rxId={key}
                              current={row[c] != null ? String(row[c]) : null}
                              onSaved={(v) => applyCellEdit(key, 'row', c, v)}
                            />
                          : drugCatLabels.has(c)
                          ? <CategoryBadge name={row[c] != null ? String(row[c]) : null}
                              color={row[c] != null ? catColors[String(row[c])] : null} />
                          : cf && rxId
                          // CV-2c: custom fields are EDITABLE inline by their declared type —
                          // date input for dates, dropdown for choice fields, text otherwise.
                          // Writes rx_custom_field_values via PUT rx/{id}/custom-fields using
                          // the TRUE rx_items.id (__rx_id), CV-1e anti-revert pattern inside.
                          ? <CustomFieldCell
                              fieldDefId={cf.field_def_id}
                              def={cfDefs[cf.field_def_id]}
                              dataType={cf.data_type}
                              rxId={rxId}
                              current={row[c] != null ? String(row[c]) : null}
                              onSaved={(v) => applyCellEdit(rxId, 'rx', c, v)}
                            />
                          : cf
                          ? (row[c] == null || String(row[c]) === ''
                              ? <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>—</span>
                              : cf.data_type === 'date'
                              ? <span title={String(row[c])}>{formatCell(c, row[c])}</span>
                              : <WorkBenchPill value={String(row[c])} />)
                          : dt === 'date'
                          ? <span title={row[c] != null ? String(row[c]) : undefined}>{formatCell(c, row[c])}</span>
                          : <span title={row[c] != null ? String(row[c]) : undefined}>{formatCell(c, row[c])}</span>}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize}
             onPage={setPage} showPageSize={false} label="rows" />
      {drawerId && (() => {
        const row = drawerRow
        const cols = data?.columns ?? []
        const rxLabel = cols.find(c => /^rx\s*#$|rx\s*number/i.test(c))
        const patLabels = cols.filter(c => /patient (first|last) name|^patient$|patient name/i.test(c))
        const fields: DrawerField[] = row ? visibleColumns
          .filter(c => !wbByLabel[c])
          .map(c => ({
            label: c,
            node: drugCatLabels.has(c)
              ? <CategoryBadge name={row[c] != null ? String(row[c]) : null}
                  color={row[c] != null ? catColors[String(row[c])] : null} />
              : <span>{row[c] == null || String(row[c]) === '' ? '—' : formatCell(c, row[c])}</span>,
          })) : []
        const wbFields = row ? visibleColumns
          .filter(c => !!wbByLabel[c])
          .map(c => ({ key: wbByLabel[c].field_key, label: c, current: row[c] != null ? String(row[c]) : null })) : []
        return (
          <RxRowDrawer
            title={row && rxLabel && row[rxLabel] != null ? String(row[rxLabel]) : 'Prescription'}
            subtitle={row ? patLabels.map(l => row[l]).filter(Boolean).join(' ') || undefined : undefined}
            fields={fields}
            wbFields={wbFields}
            wbDefs={wbDefs}
            // Same rule as the grid cell above: the drawer edits, and opens, the row's FILL
            // when the view is fill-grain.
            wbRxId={(row && rowKey(row)) ?? drawerId}
            openHref={`/rx/${(row && rowKey(row)) ?? drawerId}`}
            onClose={closeDrawer}
            notFound={!row}
            // A drawer save patches the grid row in place (same path as an inline cell edit),
            // so the list reflects it immediately — not on the next refresh cycle.
            onWbSaved={(fieldKey, v) => {
              const label = (data?.columns ?? []).find(c => wbByLabel[c]?.field_key === fieldKey)
              if (label) applyCellEdit((row && rowKey(row)) ?? drawerId, 'row', label, v)
            }}
          />
        )
      })()}
      {showBulk && (
        <BulkFieldEditor
          // Same two-grain split as the queue: bulkSel holds ROW keys — the FILL at fill grain,
          // which is where a WorkBench write belongs — and each is paired with its PRESCRIPTION,
          // which is what custom-field values key on. A base row or an rx-grain view has no fill,
          // and its key is the prescription, which is then correct for both.
          rxIds={bulkSel.map(k => bulkRx.current.get(k) ?? k)}
          wbIds={bulkSel}
          // BULK-2: the dialog shows the CHECKED ROWS' COMPLETE DATA (this view's visible
          // columns, formatted like the grid) at its bottom.
          recordsTable={{
            columns: visibleColumns,
            rows: bulkSel.map(rid => {
              const row = (data?.rows ?? []).find(r => rowKey(r) === rid)
              return {
                id: rid,
                cells: visibleColumns.map(c => row
                  ? (row[c] == null || String(row[c]) === '' ? '—' : formatCell(c, row[c]))
                  : '…'),
              }
            }),
          }}
          onClose={() => setShowBulk(false)}
          onApplied={() => loadView(true, true)} />
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: '6px 10px', textAlign: 'left', fontWeight: 600,
  borderBottom: '2px solid var(--color-border)', whiteSpace: 'nowrap',
  // sticky doubles as the containing block for the absolutely-positioned resize handle.
  background: 'var(--color-surface)', position: 'sticky', top: 0,
  // D-VIEWS-COLUMN-RESIZE: clip so narrowing a column visibly narrows it.
  overflow: 'hidden', textOverflow: 'ellipsis',
}
const tdStyle: React.CSSProperties = {
  padding: '5px 10px', whiteSpace: 'nowrap', maxWidth: 200,
  overflow: 'hidden', textOverflow: 'ellipsis',
  userSelect: 'text',   // allow copying cell text (e.g. Rx number) into PioneerRx
}
const errStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '12px 16px',
  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
  color: '#991b1b', fontSize: 13, margin: '0 24px',
}
const refreshBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 11, cursor: 'pointer',
}


// CV-2c: inline editor for a custom-field value in a view. Renders by declared type:
// date -> date input, choice (dropdown_options) -> select, else text. Saves on change/blur
// via PUT rx/{id}/custom-fields (rx_custom_field_values, the store views read). Uses the
// CV-1e lastSaved guard so a stale parent prop can't revert a successful save.
function CustomFieldCell({ fieldDefId, def, dataType, rxId, current, onSaved }: {
  fieldDefId: string
  def?: api.CustomFieldDef
  dataType: string
  rxId: string
  current: string | null
  onSaved?: (v: string | null) => void
}) {
  const [value, setValue] = useState<string | null>(current)
  const [saving, setSaving] = useState(false)
  const lastSaved = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (lastSaved.current !== undefined && current !== lastSaved.current) return
    lastSaved.current = undefined
    setValue(current)
  }, [current])

  const opts: api.DropdownOption[] = useMemo(() => {
    if (!def?.dropdown_options) return []
    try {
      const arr = JSON.parse(def.dropdown_options)
      return (arr as any[]).map(o => typeof o === 'string' ? { label: o } : o).filter(o => o?.label)
    } catch { return [] }
  }, [def?.dropdown_options])

  async function save(v: string | null) {
    setSaving(true)
    const prev = value
    setValue(v)
    try {
      const body: any = { field_id: fieldDefId }
      if (dataType === 'date') body.value_date = v || null
      else if (dataType === 'number') body.value_number = v == null || v === '' ? null : Number(v)
      else if (dataType === 'boolean') body.value_bool = v == null || v === '' ? null : v === 'true'
      else body.value_text = v || null
      await api.setRxCustomFieldValue(rxId, body)
      lastSaved.current = v
      onSaved?.(v)
    } catch { setValue(prev); lastSaved.current = undefined }
    finally { setSaving(false) }
  }

  const baseStyle: React.CSSProperties = {
    fontSize: 12, padding: '2px 6px', borderRadius: 8,
    border: '1px solid var(--color-border)', background: '#fff',
    color: 'var(--color-text)', cursor: saving ? 'wait' : 'pointer', maxWidth: 160,
  }
  const stop = { onClick: (e: React.MouseEvent) => e.stopPropagation(), onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() }

  if (dataType === 'date') {
    // value may arrive as ISO datetime — date input wants yyyy-MM-dd.
    const d = value ? String(value).slice(0, 10) : ''
    return <input type="date" value={d} disabled={saving} {...stop}
      onChange={e => save(e.target.value === '' ? null : e.target.value)} style={baseStyle} />
  }
  if (opts.length > 0) {
    const sel = opts.find(o => o.label === value) ?? null
    return (
      <span {...stop} style={{ display: 'inline-block' }}>
        <SearchableSelect
          value={value ?? ''}
          onChange={v => save(v === '' ? null : v)}
          options={[{ value: '', label: '—' }, ...opts.map(o => ({ value: o.label, label: o.label }))]}
          placeholder="—"
          ariaLabel="Custom field value"
          style={{ ...baseStyle, fontWeight: 600, borderRadius: 16,
                   background: sel?.color || '#fff', color: sel?.text || 'var(--color-text)', minWidth: 110 }}
        />
      </span>
    )
  }
  return <input type="text" defaultValue={value ?? ''} disabled={saving} placeholder="—" {...stop}
    onBlur={e => { const v = e.target.value.trim(); if (v !== (value ?? '')) save(v === '' ? null : v) }}
    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
    style={{ ...baseStyle, cursor: 'text', minWidth: 90 }} />
}
